import moment from 'moment-timezone'
import humanizeDuration from 'humanize-duration'
import { DataSet } from 'vis-data/peer'

export const UNASSIGNED_GROUP_ID = 'unassigned'

export function visItems(bookings) {
  return new DataSet(bookings.map(scheduledBookingToVisItem))
}

export function visGroups(drivers) {
  return new DataSet(
    drivers.map((driver) => ({
      id: driver.id,
      content: driver.name,
      vehicleModel: driver.vehicle_model || '',
      vehicleNumber: driver.vehicle_number || '',
    }))
  )
}

export function unassignedGroup(t, visible = false) {
  return {
    id: UNASSIGNED_GROUP_ID,
    content: t('drivers.new_layout_unassigned_group'),
    vehicleModel: '',
    vehicleNumber: '',
    visible,
  }
}

export function bookingToVisItem(booking) {
  const start = toFakeLocal(
    moment.tz(booking.ordered_at, booking.timezone)
  ).toDate()
  const duration = booking.calculated?.time ?? 0
  const end = new Date(start.getTime() + duration * 1000)

  return {
    id: booking.id,
    start,
    end,
    timezone: booking.timezone,
    duration,
    driverId: booking.driver_id ?? null,
    group: booking.driver_id ?? UNASSIGNED_GROUP_ID,
    type: 'range',

    origin: {
      address: booking.origin?.address || booking.route?.origin?.address,
      is_airport: booking.origin !== null,
    },
    destination: {
      address:
        booking.destination?.address || booking.route?.destination?.address,
      is_airport: booking.destination !== null,
    },
    luggage: booking.details?.luggage ?? 0,
    number: booking.number,
    passengers: booking.details?.passengers ?? 1,
    rideType: booking.ride_type,
    vehicleType: booking.vehicle?.name ?? '',

    className: `${booking.ride_type} ${booking.color ? 'custom-bg' : ''}`,
    style: `--item-bg: ${booking.details?.schedule_color}; background-color: ${booking.details?.schedule_color};`,
  }
}

function scheduledBookingToVisItem(booking) {
  const start = toFakeLocal(moment.tz(booking.start, booking.timezone)).toDate()
  const end = toFakeLocal(moment.tz(booking.end, booking.timezone)).toDate()

  return {
    id: booking.id,
    start,
    end,
    timezone: booking.timezone,
    duration: booking.duration,
    driverId: booking.driver_id,
    group: booking.driver_id ?? UNASSIGNED_GROUP_ID,
    type: 'range',

    origin: booking.origin,
    destination: booking.destination,
    luggage: booking.luggage,
    number: booking.number,
    passengers: booking.passengers,
    rideType: booking.ride_type,
    vehicleType: booking.vehicle_type,

    className: `${booking.ride_type} ${booking.color ? 'custom-bg' : ''}`,
    style: `--item-bg: ${booking.color}; background-color: ${booking.color};`,
  }
}

export function visItemTooltip(item, t) {
  const duration = humanizeDuration(item.duration * 1000, {
    units: ['h', 'm'],
    round: true,
  })
  const start = moment(item.start).format('lll')
  return `
    <h4 class="tooltip-header">#${item.number}</h4>
    <div class="tooltip-content">
      <div class="tooltip-row">
        <span class="tooltip-label">${t('bookings.pickup_date')}:</span>
        <span class="tooltip-value">${start}</span>
      </div>
      <div class="tooltip-row">
        <span class="tooltip-label">${t('bookings.new_layout_timezone')}:</span>
        <span class="tooltip-value">${item.timezone}</span>
      </div>
      <div class="tooltip-row">
        <span class="tooltip-label">${t('bookings.time')}:</span>
        <span class="tooltip-value">${duration}</span>
      </div>
      <div class="tooltip-row">
        <span class="tooltip-label">${t('bookings.from')}:</span>
        <span class="tooltip-value">${item.origin.address}</span>
      </div>
      <div class="tooltip-row">
        <span class="tooltip-label">${t('bookings.to')}:</span>
          <span class="tooltip-value">${item.destination.address}</span>
      </div>
      <div class="tooltip-row">
        <span class="tooltip-label">
          ${t('attributes.details.passengers.label')}:
        </span>
        <span class="tooltip-value">${item.passengers}</span>
      </div>
      <div class="tooltip-row">
        <span class="tooltip-label">
          ${t('attributes.details.luggage.label')}:
        </span>
        <span class="tooltip-value">${item.luggage}</span>
      </div>
      <div class="tooltip-row">
        <span class="tooltip-label">${t('bookings.vehicle_class')}:</span>
        <span class="tooltip-value">${item.vehicleType}</span>
      </div>
    </div>`
}

export function visItemTemplate(item) {
  const start = moment(item.start).format('HH:mm')
  const end = moment(item.end).format('HH:mm')
  const originIcon = item.origin.is_airport ? 'airplane2' : ''
  const destinationIcon = item.destination.is_airport ? 'airplane2' : ''
  return `
    <div class="start">${start}</div>
    <div class="origin-icon"><i class="icon-${originIcon}"></i></div>
    <div class="origin">${item.origin.address}</div>
    <div class="end">${end}</div>
    <div class="destination-icon"><i class="icon-${destinationIcon}"></i></div>
    <div class="destination">${item.destination.address}</div>
    <div class="passengers"><i class="icon-man"></i> ${item.passengers}</div>
    <div class="luggage"><i class="icon-briefcase3"></i> ${item.luggage}</div>
    <div class="vehicle_type"><i class="icon-car"></i> ${item.vehicleType}</div>
  `
}

export function visGroupTemplate(group) {
  if (!group) {
    return ''
  }
  return `
    <div class="driver-name">${group.content}</div>
    <div class="vehicle-info">
      <div class="model">${group.vehicleModel}</div>
      <div class="plate">${group.vehicleNumber}</div>
    </div>
  `
}

export function visOptions(override) {
  return {
    dataAttributes: ['color'],
    editable: {
      add: true,
      updateGroup: true,
    },
    format: {
      minorLabels: {
        hour: 'HH:mm',
      },
      majorLabels: {
        hour: 'D MMM YYYY',
      },
    },
    groupHeightMode: 'auto',
    groupOrder: (a, b) => {
      if (a.id === UNASSIGNED_GROUP_ID) return -1
      if (b.id === UNASSIGNED_GROUP_ID) return 1
      return a.content.localeCompare(b.content)
    },
    groupTemplate: visGroupTemplate,
    horizontalScroll: true,
    horizontalScrollInvert: true,
    itemsAlwaysDraggable: {
      item: true,
    },
    locale: 'en',
    moveable: true,
    orientation: 'top',
    selectable: true,
    showCurrentTime: true,
    showMajorLabels: true,
    stack: true,
    template: visItemTemplate,
    timeAxis: {
      scale: 'hour',
      step: 1,
    },
    tooltip: {
      followMouse: true,
      overflowMethod: 'cap',
    },
    xss: {
      disabled: true,
    },
    zoomable: false,
    zoomMax: 1000 * 60 * 60 * 24,
    zoomMin: 1000 * 60 * 60 * 24,

    ...override,
  }
}

function toFakeLocal(m) {
  const localOffset = moment().utcOffset()
  const targetOffset = m.utcOffset()
  const diff = targetOffset - localOffset
  return m.clone().add(diff, 'minutes')
}
